import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import {
    TelephoneFill, EnvelopeAtFill, GeoAltFill, Share, Facebook, Instagram, Whatsapp,
    Clipboard, CheckCircleFill, XOctagonFill, ChevronLeft
} from 'react-bootstrap-icons';
import CarGallery from './CarGallery';
import CarEnquiryForm from './CarEnquiryForm';
import CarCard from '../Market/CarCard';
import CarStatusBadges from '../../common/CarStatusBadges';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../common/ui/notify';
import language from '../../data/language';
import {
    carTitle, display, formatDate, formatEngine, formatMileage, formatPrice, isBlank, localizeEventTitle, toImageArray, translateSpec
} from '../../common/format';
import './CarDetails.css';
import '../../common/ui/ui.css';

const CarDetails = () => {
    const lang = currentLanguage();
    const strings = language[lang].details;
    const marketStrings = language[lang].market;
    const common = language[lang].common;

    const {id} = useParams();

    const [car, setCar] = useState(null);
    const [similar, setSimilar] = useState([]);
    const [status, setStatus] = useState("loading"); // loading | ready | notfound | error
    const [shareOpen, setShareOpen] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        setStatus("loading");
        setCar(null);
        setSimilar([]);

        axios.get(`/api/cars/${id}`, {signal: controller.signal})
            .then((res) => {
                setCar(res.data.data);
                setStatus("ready");
            })
            .catch((error) => {
                if (axios.isCancel(error) || error.name === 'CanceledError') return;
                // A missing car is a normal outcome, not an error to shout about.
                const code = error.response && error.response.status;
                setStatus(code === 404 || code === 400 ? "notfound" : "error");
            });

        window.scrollTo({top: 0, behavior: "smooth"});

        return () => controller.abort();
    }, [id]);

    // Similar cars are a nice-to-have: a failure here leaves the section hidden.
    useEffect(() => {
        if (status !== "ready") return undefined;
        const controller = new AbortController();
        axios.get(`/api/cars/${id}/similar`, {signal: controller.signal})
            .then((res) => setSimilar(Array.isArray(res.data.data) ? res.data.data : []))
            .catch(() => {});
        return () => controller.abort();
    }, [id, status]);

    const images = useMemo(() => toImageArray(car && car.images), [car]);
    const features = useMemo(() => (Array.isArray(car && car.features) ? car.features : []), [car]);

    const shareUrl = typeof window !== "undefined"
        ? `${window.location.origin}/car/${id}`
        : `https://carexpress.ge/car/${id}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            notify.success(common.copied);
        } catch (error) {
            notify.error({
                en: "Could not copy the link. Please copy it manually.",
                geo: "ბმულის კოპირება ვერ მოხერხდა. დააკოპირეთ ხელით.",
                ru: "Не удалось скопировать ссылку. Скопируйте вручную.",
                am: "Չհաջողվեց պատճենել հղումը։"
            });
        }
    };

    const openInstagram = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            notify.info({
                en: "Link copied — paste it in your Instagram story or DM.",
                geo: "ბმული დაკოპირდა — ჩასვით Instagram-ის სთორიში ან მესიჯში.",
                ru: "Ссылка скопирована — вставьте её в историю или сообщение Instagram.",
                am: "Հղումը պատճենվեց — տեղադրեք Instagram-ում։"
            });
        } catch (error) {
            // Copy is a convenience; still open Instagram.
        }
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    };

    if (status === "loading") {
        return (
            <div className="car-details-page">
                <div className="container">
                    <LoadingBlock label={common.loading} />
                </div>
            </div>
        );
    }

    if (status === "notfound") {
        return (
            <div className="car-details-page">
                <div className="container">
                    <EmptyBlock
                        title={strings.not_found_title}
                        message={strings.not_found_message}
                        action={<Link to="/market" className="cx-btn cx-btn-primary">{strings.back_to_market}</Link>}
                    />
                </div>
            </div>
        );
    }

    if (status === "error" || !car) {
        return (
            <div className="car-details-page">
                <div className="container">
                    <ErrorBlock
                        title={common.error_title}
                        message={common.error_message}
                        retryLabel={common.retry}
                        onRetry={() => window.location.reload()}
                    />
                </div>
            </div>
        );
    }

    const sold = Number(car.sold) === 1;
    const inAuction = Number(car.in_auction) === 1;
    const auctionSticker = lang === "geo" ? "/auction-sticker-ka.svg" : "/auction-sticker-en.svg";
    const title = carTitle(car);

    // Only rows that actually hold a value are rendered.
    const specs = [
        {label: strings.mark, value: car.mark},
        {label: strings.model, value: car.model},
        {label: strings.year, value: car.year},
        {label: "VIN", value: car.vin},
        {label: strings.category, value: translateSpec("category", car.category, lang)},
        {label: strings.millage, value: isBlank(car.millage) ? null : formatMileage(car.millage)},
        {label: strings.fuel_type, value: translateSpec("fuel", car.fuel, lang)},
        {label: strings.engine, value: isBlank(car.engine) ? null : formatEngine(car.engine)},
        {label: strings.cilinders, value: car.cilinder},
        {label: strings.gear_box, value: translateSpec("transmission", car.transmission, lang)},
        {label: strings.drive_type, value: translateSpec("drive", car.drive, lang)},
        {label: strings.doors, value: car.doors},
        {label: strings.airbags, value: car.airbags},
        {label: strings.color, value: translateSpec("color", car.color, lang)},
        {label: strings.lot, value: car.lot},
        {label: strings.auction, value: localizeEventTitle(car.auction, lang)},
        {label: strings.location, value: translateSpec("location", car.location, lang)},
        {label: strings.published_at, value: isBlank(car.create_date) ? null : formatDate(car.create_date)}
    ].filter((row) => !isBlank(row.value));

    return (
        <div className="car-details-page">
            <div className="container">
                <nav className="car-breadcrumbs" aria-label="breadcrumb">
                    <Link to="/">{language[lang].header.main}</Link>
                    <span aria-hidden="true">/</span>
                    <Link to="/market">{language[lang].header.market}</Link>
                    <span aria-hidden="true">/</span>
                    <span className="car-breadcrumbs-current">{title}</span>
                </nav>

                <Link to="/market" className="car-back-link">
                    <ChevronLeft aria-hidden="true" /> {strings.back_to_market}
                </Link>

                <div className="car-details-layout">
                    <main className="car-details-main">
                        <header className="car-details-header">
                            <div>
                                <h1 className="car-details-title">{title}</h1>
                                <div className="car-details-tags">
                                    <span className={`cx-badge ${sold ? "cx-badge-sold" : "cx-badge-sale"}`}>
                                        {sold ? <XOctagonFill aria-hidden="true" /> : <CheckCircleFill aria-hidden="true" />}
                                        {sold ? marketStrings.status_sold : marketStrings.status_for_sale}
                                    </span>
                                    {!isBlank(car.location) && (
                                        <span className="car-details-location">
                                            <GeoAltFill aria-hidden="true" /> {translateSpec("location", display(car.location), lang)}
                                        </span>
                                    )}
                                </div>
                                <CarStatusBadges car={car} lang={lang} />
                            </div>
                            <button type="button" className="cx-btn cx-btn-ghost car-share-button" onClick={() => setShareOpen(true)}>
                                <Share aria-hidden="true" /> {strings.share}
                            </button>
                        </header>

                        <CarGallery images={images} alt={title} cornerBadge={inAuction ? auctionSticker : null} />

                        {specs.length > 0 && (
                            <section className="car-section">
                                <h2 className="car-section-title">{strings.specifications}</h2>
                                <dl className="car-spec-grid">
                                    {specs.map((row) => (
                                        <div className="car-spec-row" key={row.label}>
                                            <dt>{row.label}</dt>
                                            <dd>{display(row.value)}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </section>
                        )}

                        {!isBlank(car.description) && (
                            <section className="car-section">
                                <h2 className="car-section-title">{strings.description}</h2>
                                {/* Rendered as text, never as HTML — the description is
                                    admin-entered free text and must not become markup. */}
                                <p className="car-description">{display(car.description)}</p>
                            </section>
                        )}

                        {features.length > 0 && (
                            <section className="car-section">
                                <h2 className="car-section-title">{strings.features}</h2>
                                <ul className="car-feature-list">
                                    {features.map((feature, index) => (
                                        <li key={`${feature}-${index}`} className="car-feature">{feature}</li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </main>

                    <aside className="car-details-aside">
                        <div className="car-price-card">
                            <span className="car-price-label">{strings.price}</span>
                            <p className="car-price-value">{formatPrice(car.price, car.currency)}</p>

                            {!isBlank(car.phone) && (
                                <a href={`tel:${String(car.phone).replace(/\s/g, "")}`} className="cx-btn cx-btn-primary cx-btn-block car-call-button">
                                    <TelephoneFill aria-hidden="true" /> {display(car.phone)}
                                </a>
                            )}

                            <div className="car-contact-list">
                                <a href="mailto:info@carexpress.ge"><EnvelopeAtFill aria-hidden="true" /> info@carexpress.ge</a>
                                <a href="tel:0322424100"><TelephoneFill aria-hidden="true" /> 032-242-41-00</a>
                            </div>
                        </div>

                        <div className="car-enquiry-card">
                            <CarEnquiryForm carId={car.id} carLabel={title} />
                        </div>
                    </aside>
                </div>

                {similar.length > 0 && (
                    <section className="car-section car-similar">
                        <h2 className="car-section-title">{strings.similar_cars}</h2>
                        <div className="car-similar-grid">
                            {similar.slice(0, 4).map((item) => (
                                <div className="car-similar-item" key={item.id}>
                                    <CarCard car={item} />
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <Modal show={shareOpen} centered size="md" onHide={() => setShareOpen(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{strings.share}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="car-share-link">
                        <span className="car-share-url">{shareUrl}</span>
                        <button type="button" className="cx-btn cx-btn-subtle cx-btn-sm" onClick={copyLink}>
                            <Clipboard aria-hidden="true" /> {common.copy}
                        </button>
                    </div>
                    <div className="car-share-buttons">
                        <a className="car-share-icon" title="Facebook" target="_blank" rel="noreferrer"
                           href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}>
                            <Facebook />
                        </a>
                        <button type="button" className="car-share-icon" title="Instagram" onClick={openInstagram}>
                            <Instagram />
                        </button>
                        <a className="car-share-icon" title="WhatsApp" target="_blank" rel="noreferrer"
                           href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}>
                            <Whatsapp />
                        </a>
                        <a className="car-share-icon" title="Viber" target="_blank" rel="noreferrer"
                           href={`viber://forward?text=${encodeURIComponent(shareUrl)}`}>
                            <img src="/static/viber.jpg" alt="Viber" />
                        </a>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default CarDetails;
